/**
 * Phase 3a PR 2-2 — PasskeyAutoFlow 글로벌 컨테이너 (Refs: #1117, Epic #1110).
 *
 * layouts/index.js의 Fragment 안 BootstrappedLayout() 외부에 마운트되어
 * 모든 라우팅에 살아있음. user.passkeyAutoFlow Redux state를 구독하여:
 *  - 'register'         → PasskeyRegisterModal (가입/changePW 후 등록 권유)
 *  - null               → 렌더 X
 *
 * ⚠️ 트리거 제거됨 (Issue #1317): 가입/changePW/invite 가입 직후 자동 등록 권유를 UX 결정으로
 * 폐지 — 현재 passkeyAutoFlow 생산자 0 (본 컨테이너는 항상 null 렌더). 재도입 대비 잔존.
 * 재도입 시 redux/user/sagas.js 의 UMK_LOGIN(invite)/UMK_REGISTER/UMK_CHANGEPW 에 있던
 * 트리거 블록을 복원하면 됨 (#1317 PR diff 참조). 패스키 등록은 Settings 수동 경로만 사용.
 *
 * DBE Keeper(#1291, v1.5): 'recalc'/'recalc-from-839' 폐기 — PRF wrapped_umk 재계산이 사라지고
 * MPW 변경 시 서버가 디바이스를 일괄 revoke 하므로 재인증 모달 불필요 (설계 §1.2.1).
 *
 * 보안 보강:
 *  - getUMKFromLocal Promise reject 시 trigger flag 초기화 (무한 재시도 방지)
 *  - UMK null 시 modal 안 띄움 + flag clear
 *  - autoLock 만료 시 setTimeout으로 modal 자동 닫기 (React state 평문 잔존 방지)
 */
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getUMKFromLocal } from 'pUtils'
import { getEnvParam } from 'env'

import { isPasskeyEnabled } from 'services/passkey'
import { maybeRegisterCurrentDevice } from 'services/passkey/deviceUnlock'
import PasskeyRegisterModal from 'pages/auth/components/PasskeyRegisterModal'

const PasskeyAutoFlow = () => {
  const dispatch = useDispatch()
  const passkeyAutoFlow = useSelector((state) => state.user?.passkeyAutoFlow)
  const [umk, setUmk] = useState(null)

  // Feature flag off면 어떤 trigger도 무시 (#1117 후속)
  const enabled = isPasskeyEnabled()

  const clearFlow = useCallback(() => {
    dispatch({ type: 'user/SET_STATE', payload: { passkeyAutoFlow: null } })
  }, [dispatch])

  useEffect(() => {
    if (!enabled || !passkeyAutoFlow) {
      setUmk(null)
      return undefined
    }

    let cancelled = false
    getUMKFromLocal(false)
      .then((value) => {
        if (cancelled) return
        if (!value) {
          // UMK 만료/없음 — modal 표시 X, flag 초기화
          clearFlow()
          return
        }
        setUmk(value)
      })
      .catch(() => {
        if (!cancelled) clearFlow()
      })

    // autoLock 만료 시 modal 강제 닫기 + state clear
    // umkDuration은 분 단위, 0이면 무한 (timer 미설치)
    const autoLockMinutes = Number(getEnvParam('umkDuration') || 0)
    const autoLockMs = autoLockMinutes > 0 ? autoLockMinutes * 60 * 1000 : 0
    let timerId = null
    if (autoLockMs > 0) {
      timerId = setTimeout(() => {
        if (!cancelled) clearFlow()
      }, autoLockMs)
    }

    return () => {
      cancelled = true
      if (timerId) clearTimeout(timerId)
    }
  }, [enabled, passkeyAutoFlow, clearFlow])

  if (!enabled || !passkeyAutoFlow || !umk) {
    return null
  }

  if (passkeyAutoFlow === 'register') {
    return (
      <PasskeyRegisterModal
        open
        umk={umk}
        onClose={clearFlow}
        onRegistered={async (result) => {
          // PR-2 (#1301, 설계 §5.2-(3)): 가입/changePW 후 권유 경로에서도 패스키 등록 직후
          // 이 브라우저 디바이스 자동 등록 (토큰으로 프롬프트 0회). 실패해도 flow 는 항상 종료.
          // #1319 ⑦: "항상 종료" 보장을 maybeRegister 내부 catch 에만 의존하지 않도록 finally 로 명시.
          try {
            await maybeRegisterCurrentDevice(umk, result)
          } finally {
            clearFlow()
          }
        }}
      />
    )
  }

  return null
}

export default PasskeyAutoFlow
